/**
 * Pure facts for pushing a master config onto its containers (21/10/2026):
 * which followers own a section (every section when none is named) — the
 * shared pushableSections, so a route test's own gateways never count — and
 * what each one gives back, in words ("Default amount 802 → 500").
 * For Media Devoted | Created By Ciro Autiero
 */
import { type DropGenSection, pushableSections } from '@trafficpulse/shared';

import type { DropGenContainerSummary, DropGenPlanDetail } from '../../../../drop-generator.types';
import { followersOf, SECTION_LABEL } from '../../../campaign/master/rule-facts';

/** The master's values a push hands out — the editor's draft, saved first when edited. */
export interface MasterValues {
  defaultAmount: number;
  failMode: string;
}

export interface PushOwner {
  container: DropGenContainerSummary;
  sections: DropGenSection[];
}

/** The followers a push of `sections` (all when absent) would change, in board order. */
export function pushOwners(plan: DropGenPlanDetail, masterId: string, sections?: readonly DropGenSection[]): PushOwner[] {
  return followersOf(plan, masterId)
    .map((container) => ({ container, sections: pushableSections(container, sections) }))
    .filter((o) => o.sections.length > 0)
    .sort((a, b) => a.container.orderIndex - b.container.orderIndex);
}

const sms = (n: number) => `${n.toLocaleString('en-US')} SMS`;
const failWord = (mode: string) => (mode === 'STOP_FOR_DAY' ? 'stop list' : 'complete list');

function ownAutoSendWords(c: DropGenContainerSummary): string {
  const cap = c.ownAutoSend?.dailySmsCap;
  return cap === null || cap === undefined ? 'its own auto-send' : `its own auto-send (cap ${sms(cap)})`;
}

/** One phrase per section the container gives back. */
export function givenBack(owner: PushOwner, master: MasterValues): string[] {
  const c = owner.container;
  return owner.sections.map((s) => {
    if (s === 'amount') return `${SECTION_LABEL.amount} ${sms(c.defaultAmount)} → ${sms(master.defaultAmount)}`;
    if (s === 'failMode') return `${SECTION_LABEL.failMode} ${failWord(c.failMode)} → ${failWord(master.failMode)}`;
    if (s === 'autoSend') return ownAutoSendWords(c);
    return SECTION_LABEL[s];
  });
}

/** The container's own value in a few words, for the marker's hover ("802 SMS"); null = only "its own". */
export function ownValueWords(c: DropGenContainerSummary, section: DropGenSection): string | null {
  if (section === 'amount') return sms(c.defaultAmount);
  if (section === 'failMode') return failWord(c.failMode);
  if (section === 'autoSend') return c.ownAutoSend?.dailySmsCap != null ? `cap ${sms(c.ownAutoSend.dailySmsCap)}` : null;
  return null;
}
